import { useEffect } from 'react';
import type { Grade } from '../lib/scheduler';

interface Props {
  onGrade: (grade: Extract<Grade, 'missed' | 'got' | 'instant'>) => void;
  disabled?: boolean;
  /** Instant needs evidence: not after a hint, and not without a recording of the attempt. */
  noInstant?: false | 'hint' | 'unrecorded';
}

const GRADES = [
  { grade: 'missed', label: 'Missed', help: "Couldn't, or a word wrong", key: '1' },
  { grade: 'got', label: 'Got it', help: 'Every word; stress may slip', key: '2' },
  { grade: 'instant', label: 'Instant', help: 'First try, no hint', key: '3' },
] as const;

/** Self-grading with a written rubric, so "Missed" means the same thing every time. Keys 1–3 work too. */
export function GradeBar({ onGrade, disabled, noInstant }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled || e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.target as HTMLElement)?.closest('input, textarea, dialog[open]')) return;
      const g = GRADES.find((x) => x.key === e.key);
      if (g && !(noInstant && g.grade === 'instant')) {
        e.preventDefault();
        onGrade(g.grade);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onGrade, disabled, noInstant]);

  return (
    <div className="stack" style={{ gap: 8 }}>
      <p className="small muted" id="grade-q" style={{ textAlign: 'center' }}>
        How did it go?
      </p>
      <div className="grades" role="group" aria-labelledby="grade-q">
        {GRADES.map((g) => (
          <button
            key={g.grade}
            type="button"
            className={`grade grade-${g.grade}`}
            onClick={() => onGrade(g.grade)}
            disabled={disabled || (noInstant && g.grade === 'instant')}
          >
            {g.label}
            <small>{g.grade === 'instant' && noInstant ? (noInstant === 'hint' ? 'Not after a hint' : 'Only after recording') : g.help}</small>
          </button>
        ))}
      </div>
    </div>
  );
}
